import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { APIProvider, Map, Marker } from '@vis.gl/react-google-maps';
import { Button, Card, InputNumber, Checkbox, Tag, Space, Tooltip, Input } from 'antd';
import {
  BorderOutlined,
  EnvironmentOutlined,
  GoldOutlined,
  DeleteOutlined,
  EyeOutlined,
  EditOutlined,
  PushpinOutlined,
  ArrowRightOutlined,
  ArrowDownOutlined,
  PlusSquareOutlined
} from '@ant-design/icons';
import { useAuth } from '../AuthContext';
import Polyline from './Polyline';
import Polygon from './Polygon';
import PoleListModal from './PoleListModal';
import GPSPointsModal from './GPSPointsModal';
import PlantGroupModal from './PlantGroupModal';
import PlantListModal from './PlantListModal';
import TodoModal from './TodoModal';
import { calculatePolygonArea, calculateLineLength, calculateDistance } from '../algorithms/geoCalculations';
import { calculatePolePositions } from '../algorithms/poleCalculations';
import { calculateCropAreaFromPoints, cloneCropArea, cloneCropAreaLongitudinal, cloneCropAreaBidirectional } from '../algorithms/cropAreaCalculations';
import { calculatePlantPositions } from '../algorithms/plantCalculations';
import theme from '../theme';

function MapComponent() {
  const { mapState, saveMapState, saveBorderData, borderData, saveGamefenceData, gamefenceData, poleDistance, savePoleDistance, gamefenceClosed, saveGamefenceClosed, cropAreas, saveCropAreas, cropAreaWidth, saveCropAreaWidth, separationWidth, saveSeparationWidth, separationWidthLongitudinal, saveSeparationWidthLongitudinal, gpsPoints, saveGpsPoints, plantGroups, savePlantGroups, saveTodoEntry } = useAuth();
  const [borderPoints, setBorderPoints] = useState(borderData || []);
  const [gamefencePoints, setGamefencePoints] = useState(gamefenceData || []);
  const [currentCenter, setCurrentCenter] = useState(mapState.center);
  const [currentZoom, setCurrentZoom] = useState(mapState.zoom);
  const [currentMapTypeId, setCurrentMapTypeId] = useState(mapState.mapTypeId);
  const [selectedTool, setSelectedTool] = useState(null);
  const [draggedMarker, setDraggedMarker] = useState(null);
  const [showPoleList, setShowPoleList] = useState(false);
  const [showGpsPointsModal, setShowGpsPointsModal] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState('');
  const [cropAreaPoints, setCropAreaPoints] = useState([]);
  const [selectedCropAreaId, setSelectedCropAreaId] = useState(null);
  const [editingCropAreaId, setEditingCropAreaId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [snapToGpsEnabled, setSnapToGpsEnabled] = useState(true);
  const [snapDistance, setSnapDistance] = useState(5); // meters
  const [showPlantGroupModal, setShowPlantGroupModal] = useState(false);
  const [showPlantListModal, setShowPlantListModal] = useState(false);
  const [showTodoModal, setShowTodoModal] = useState(false);
  const [editingPlantGroup, setEditingPlantGroup] = useState(null);
  const [selectedPlantGroupForTodo, setSelectedPlantGroupForTodo] = useState(null);
  const [selectedPlantGroupId, setSelectedPlantGroupId] = useState(null);

  // Find nearest GPS point within snap distance
  const findNearestGpsPoint = useCallback((position) => {
    if (!snapToGpsEnabled || gpsPoints.length === 0) {
      return null;
    }

    let nearestPoint = null;
    let minDistance = snapDistance;

    for (const gpsPoint of gpsPoints) {
      const distance = calculateDistance(position, gpsPoint);
      if (distance < minDistance) {
        minDistance = distance;
        nearestPoint = gpsPoint;
      }
    }

    return nearestPoint;
  }, [gpsPoints, snapToGpsEnabled, snapDistance]);

  const handleMapClick = useCallback((event) => {
    let lat = event.detail.latLng.lat;
    let lng = event.detail.latLng.lng;

    // Check for snap to GPS point
    const nearestPoint = findNearestGpsPoint({ lat, lng });
    if (nearestPoint) {
      lat = nearestPoint.lat;
      lng = nearestPoint.lng;
    }

    if (selectedTool === 'border') {
      const newPoints = [...borderPoints, { lat, lng }];
      setBorderPoints(newPoints);
      saveBorderData(newPoints);
    } else if (selectedTool === 'gamefence') {
      const newPoints = [...gamefencePoints, { lat, lng }];
      setGamefencePoints(newPoints);
      saveGamefenceData(newPoints);
    } else if (selectedTool === 'croparea') {
      const newPoints = [...cropAreaPoints, { lat, lng }];
      setCropAreaPoints(newPoints);

      // When we have 2 points, create the crop area with the width parameter
      if (newPoints.length === 2) {
        const cropAreaData = calculateCropAreaFromPoints(newPoints[0], newPoints[1], cropAreaWidth);

        if (cropAreaData) {
          const newCropArea = {
            id: Date.now().toString(),
            name: 'New Crop Area',
            description: 'Enter description here',
            ...cropAreaData,
            points: newPoints
          };

          const updatedCropAreas = [...cropAreas, newCropArea];
          saveCropAreas(updatedCropAreas);
          setCropAreaPoints([]);
        }
      }
    }
  }, [selectedTool, borderPoints, gamefencePoints, cropAreaPoints, cropAreas, cropAreaWidth, saveBorderData, saveGamefenceData, saveCropAreas, findNearestGpsPoint]);

  const handleMarkerDragStart = useCallback((index, tool) => {
    setDraggedMarker({ index, tool, currentPosition: null });
  }, []);

  const handleMarkerDrag = useCallback((index, event, tool) => {
    let newPoint = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    };

    // Check for snap to GPS point when dragging
    const nearestPoint = findNearestGpsPoint(newPoint);
    if (nearestPoint) {
      newPoint = { lat: nearestPoint.lat, lng: nearestPoint.lng };
    }

    // Update the dragged marker position for distance calculation
    setDraggedMarker(prev => prev ? { ...prev, currentPosition: newPoint } : null);

    if (tool === 'border') {
      const newPoints = [...borderPoints];
      newPoints[index] = newPoint;
      setBorderPoints(newPoints);
      saveBorderData(newPoints);
    } else if (tool === 'gamefence') {
      const newPoints = [...gamefencePoints];
      newPoints[index] = newPoint;
      setGamefencePoints(newPoints);
      saveGamefenceData(newPoints);
    } else if (tool === 'croparea') {
      // Find the crop area being edited
      const cropArea = cropAreas.find(area => area.id === selectedCropAreaId);
      if (cropArea) {
        const newPoints = [...cropArea.points];
        newPoints[index] = newPoint;

        // Recalculate the crop area with the new points
        const cropAreaData = calculateCropAreaFromPoints(newPoints[0], newPoints[1], cropArea.width);

        if (cropAreaData) {
          const updatedCropArea = {
            ...cropArea,
            ...cropAreaData,
            points: newPoints
          };

          const updatedCropAreas = cropAreas.map(area =>
            area.id === selectedCropAreaId ? updatedCropArea : area
          );
          saveCropAreas(updatedCropAreas);
        }
      }
    }
  }, [borderPoints, gamefencePoints, cropAreas, selectedCropAreaId, saveBorderData, saveGamefenceData, saveCropAreas, findNearestGpsPoint]);

  const handleMarkerDragEnd = useCallback(() => {
    setDraggedMarker(null);
  }, []);

  const handleCenterChanged = useCallback((event) => {
    const map = event.map;
    const center = {
      lat: map.getCenter().lat(),
      lng: map.getCenter().lng()
    };
    const zoom = map.getZoom();
    const mapTypeId = map.getMapTypeId();
    setCurrentCenter(center);
    setCurrentZoom(zoom);
    setCurrentMapTypeId(mapTypeId);
    saveMapState(center, zoom, mapTypeId);
  }, [saveMapState]);

  const handleZoomChanged = useCallback((event) => {
    const map = event.map;
    const center = {
      lat: map.getCenter().lat(),
      lng: map.getCenter().lng()
    };
    const zoom = map.getZoom();
    const mapTypeId = map.getMapTypeId();
    setCurrentCenter(center);
    setCurrentZoom(zoom);
    setCurrentMapTypeId(mapTypeId);
    saveMapState(center, zoom, mapTypeId);
  }, [saveMapState]);

  const handleMapTypeChanged = useCallback((event) => {
    const map = event.map;
    const center = {
      lat: map.getCenter().lat(),
      lng: map.getCenter().lng()
    };
    const zoom = map.getZoom();
    const mapTypeId = map.getMapTypeId();
    setCurrentCenter(center);
    setCurrentZoom(zoom);
    setCurrentMapTypeId(mapTypeId);
    saveMapState(center, zoom, mapTypeId);
  }, [saveMapState]);

  const clearBorder = () => {
    setBorderPoints([]);
    saveBorderData([]);
  };

  const clearGamefence = () => {
    setGamefencePoints([]);
    saveGamefenceData([]);
  };

  const clearCropAreaPoints = () => {
    setCropAreaPoints([]);
  };

  const handleDeleteCropArea = (cropAreaId) => {
    const updatedCropAreas = cropAreas.filter(area => area.id !== cropAreaId);
    saveCropAreas(updatedCropAreas);
    if (selectedCropAreaId === cropAreaId) {
      setSelectedCropAreaId(null);
    }
  };

  const handleSelectCropArea = (cropAreaId) => {
    setSelectedCropAreaId(cropAreaId === selectedCropAreaId ? null : cropAreaId);
  };

  const handleStartEditCropArea = (cropArea) => {
    setEditingCropAreaId(cropArea.id);
    setEditName(cropArea.name);
    setEditDescription(cropArea.description);
  };

  const handleSaveEditCropArea = (cropAreaId) => {
    const updatedCropAreas = cropAreas.map(area => {
      if (area.id === cropAreaId) {
        return { ...area, name: editName, description: editDescription };
      }
      return area;
    });
    saveCropAreas(updatedCropAreas);
    setEditingCropAreaId(null);
    setEditName('');
    setEditDescription('');
  };

  const handleCancelEditCropArea = () => {
    setEditingCropAreaId(null);
    setEditName('');
    setEditDescription('');
  };

  const handleCloneCropArea = (cropArea) => {
    const clonedAreaData = cloneCropArea(cropArea, separationWidth);

    if (clonedAreaData) {
      const newCropArea = {
        id: Date.now().toString(),
        name: `${cropArea.name} (Clone ⟂)`,
        description: cropArea.description,
        ...clonedAreaData
      };

      const updatedCropAreas = [...cropAreas, newCropArea];
      saveCropAreas(updatedCropAreas);
    }
  };

  const handleCloneCropAreaLongitudinal = (cropArea) => {
    const clonedAreaData = cloneCropAreaLongitudinal(cropArea, separationWidthLongitudinal);

    if (clonedAreaData) {
      const newCropArea = {
        id: Date.now().toString(),
        name: `${cropArea.name} (Clone ∥)`,
        description: cropArea.description,
        ...clonedAreaData
      };

      const updatedCropAreas = [...cropAreas, newCropArea];
      saveCropAreas(updatedCropAreas);
    }
  };

  const handleCloneCropAreaBidirectional = (cropArea) => {
    const clonedAreaData = cloneCropAreaBidirectional(cropArea, separationWidth, separationWidthLongitudinal);

    if (clonedAreaData) {
      const newCropArea = {
        id: Date.now().toString(),
        name: `${cropArea.name} (Clone ⟂∥)`,
        description: cropArea.description,
        ...clonedAreaData
      };

      const updatedCropAreas = [...cropAreas, newCropArea];
      saveCropAreas(updatedCropAreas);
    }
  };

  // GPS Points Management
  const handleAddGpsPoint = (pointData) => {
    const newPoint = {
      id: Date.now().toString(),
      ...pointData
    };
    const updatedPoints = [...gpsPoints, newPoint];
    saveGpsPoints(updatedPoints);
  };

  const handleEditGpsPoint = (pointId, pointData) => {
    const updatedPoints = gpsPoints.map(point =>
      point.id === pointId ? { ...point, ...pointData } : point
    );
    saveGpsPoints(updatedPoints);
  };

  const handleDeleteGpsPoint = (pointId) => {
    const updatedPoints = gpsPoints.filter(point => point.id !== pointId);
    saveGpsPoints(updatedPoints);
  };

  const selectTool = (toolName) => {
    setSelectedTool(toolName === selectedTool ? null : toolName);
    // Clear crop area points when switching tools
    if (toolName !== 'croparea') {
      setCropAreaPoints([]);
    }
  };

  const handleSavePlantGroup = (plantGroup) => {
    if (editingPlantGroup) {
      // Update existing plant group
      const updatedGroups = plantGroups.map(group =>
        group.id === plantGroup.id ? plantGroup : group
      );
      savePlantGroups(updatedGroups);
    } else {
      // Add new plant group
      savePlantGroups([...plantGroups, plantGroup]);
    }
    setEditingPlantGroup(null);
  };

  const handleEditPlantGroup = (plantGroup) => {
    setEditingPlantGroup(plantGroup);
    setShowPlantListModal(false);
    setShowPlantGroupModal(true);
  };

  const handleDeletePlantGroup = (plantGroupId) => {
    const updatedGroups = plantGroups.filter(group => group.id !== plantGroupId);
    savePlantGroups(updatedGroups);
    if (selectedPlantGroupId === plantGroupId) {
      setSelectedPlantGroupId(null);
    }
  };

  const handleAddTodoForPlantGroup = (plantGroup) => {
    setSelectedPlantGroupForTodo(plantGroup);
    setShowPlantListModal(false);
    setShowTodoModal(true);
  };

  const handleSaveTodo = async (todo) => {
    await saveTodoEntry(todo);
    setSelectedPlantGroupForTodo(null);
  };

  const handleSelectPlantGroup = (plantGroupId) => {
    setSelectedPlantGroupId(plantGroupId === selectedPlantGroupId ? null : plantGroupId);
  };

  useEffect(() => {
    setCurrentCenter(mapState.center);
    setCurrentZoom(mapState.zoom);
    setCurrentMapTypeId(mapState.mapTypeId);
  }, [mapState]);

  useEffect(() => {
    if (borderData) {
      setBorderPoints(borderData);
    }
  }, [borderData]);

  useEffect(() => {
    if (gamefenceData) {
      setGamefencePoints(gamefenceData);
    }
  }, [gamefenceData]);

  const borderArea = calculatePolygonArea(borderPoints);
  const gamefenceLength = calculateLineLength(gamefencePoints);
  const gamefenceArea = gamefenceClosed && gamefencePoints.length > 2 ? calculatePolygonArea(gamefencePoints) : 0;

  // Calculate total crop area
  const totalCropArea = cropAreas.reduce((sum, area) => sum + area.area, 0);

  // Calculate current crop area being drawn
  const currentCropArea = cropAreaPoints.length === 2 ? calculateCropAreaFromPoints(cropAreaPoints[0], cropAreaPoints[1], cropAreaWidth) : null;

  // Calculate total fence length including closing segment if fence is closed
  const totalFenceLength = gamefenceLength + (gamefenceClosed && gamefencePoints.length > 2 ?
    calculateLineLength([gamefencePoints[gamefencePoints.length - 1], gamefencePoints[0]]) : 0);

  const poleCount = totalFenceLength > 0 && poleDistance > 0 ? Math.ceil(totalFenceLength / poleDistance) + 1 : 0;

  // Calculate distance to previous point when dragging
  const dragDistance = draggedMarker ? (() => {
    const { index, tool, currentPosition } = draggedMarker;
    if (tool === 'gamefence' && index > 0) {
      const prevPoint = gamefencePoints[index - 1];
      return calculateDistance(prevPoint, currentPosition);
    } else if (tool === 'border' && index > 0) {
      const prevPoint = borderPoints[index - 1];
      return calculateDistance(prevPoint, currentPosition);
    }
    return 0;
  })() : 0;

  // Get current coordinates when dragging
  const dragCoordinates = draggedMarker && draggedMarker.currentPosition ? draggedMarker.currentPosition : null;

  // Calculate pole positions
  const polePositions = useMemo(() => {
    return gamefencePoints.length > 1 ?
      calculatePolePositions(gamefencePoints, poleDistance, gamefenceClosed) : [];
  }, [gamefencePoints, poleDistance, gamefenceClosed]);

  const copyFenceData = useCallback(async () => {
    if (polePositions.length === 0) return;

    const currentDate = new Date().toLocaleDateString();
    const currentTime = new Date().toLocaleTimeString();

    let fenceData = `GAMEFENCE POLE POSITIONS
Generated: ${currentDate} ${currentTime}

FENCE CONFIGURATION:
Pole Distance: ${poleDistance} m
Total Length: ${totalFenceLength.toFixed(2)} m
Fence Type: ${gamefenceClosed ? 'Closed' : 'Open'}
Number of Poles: ${polePositions.length}`;

    if (gamefenceClosed && gamefenceArea > 0) {
      fenceData += `
Enclosed Area: ${gamefenceArea.toLocaleString()} m² (${(gamefenceArea / 10000).toFixed(2)} hectares)`;
    }

    fenceData += `

POLE POSITIONS:
Pole #\tType\tLatitude\tLongitude\tDistance from Start (m)\tDistance from Previous (m)`;

    polePositions.forEach(pole => {
      const poleType = pole.isMarker ? `Marker ${pole.markerIndex + 1}` : 'Interval';
      fenceData += `
${pole.index}\t${poleType}\t${pole.position.lat.toFixed(6)}\t${pole.position.lng.toFixed(6)}\t${pole.distanceFromStart.toFixed(2)}\t${pole.distanceFromPrevious.toFixed(2)}`;
    });

    try {
      await navigator.clipboard.writeText(fenceData);
      setCopyFeedback('Copied to clipboard!');
      setTimeout(() => setCopyFeedback(''), 2000);
    } catch (err) {
      setCopyFeedback('Copy failed - please try again');
      setTimeout(() => setCopyFeedback(''), 2000);
    }
  }, [polePositions, poleDistance, totalFenceLength, gamefenceClosed, gamefenceArea]);

  return (
    <>
      <div className="controls" style={{
        padding: '12px 16px',
        backgroundColor: theme.colors.backgroundAlt,
        borderBottom: `1px solid ${theme.colors.lightGrey}`
      }}>
        <Space wrap size="small" style={{ width: '100%', alignItems: 'center' }}>
          <Button
            type="primary"
            icon={<PushpinOutlined />}
            onClick={() => setShowGpsPointsModal(true)}
            style={{ backgroundColor: theme.colors.gpsPoint }}
            size="small"
          >
            GPS ({gpsPoints.length})
          </Button>
          <Checkbox
            checked={snapToGpsEnabled}
            onChange={(e) => setSnapToGpsEnabled(e.target.checked)}
          >
            Snap
          </Checkbox>
          {snapToGpsEnabled && (
            <InputNumber
              value={snapDistance}
              onChange={(value) => setSnapDistance(value || 5)}
              min={1}
              max={50}
              step={1}
              style={{ width: '60px' }}
              addonAfter="m"
              size="small"
            />
          )}
          {totalCropArea > 0 && (
            <Tag color="success" style={{ margin: 0 }}>
              <strong>Crop: {(totalCropArea / 10000).toFixed(2)} ha</strong>
            </Tag>
          )}
          {borderPoints.length > 0 && (
            <Tag color="error" style={{ margin: 0 }}>
              Border: {(borderArea / 10000).toFixed(2)} ha
            </Tag>
          )}
          {gamefencePoints.length > 0 && (
            <>
              <Tag color="success" style={{ margin: 0 }}>
                Fence: {(totalFenceLength / 1000).toFixed(2)} km
              </Tag>
              {gamefenceClosed && gamefenceArea > 0 && (
                <Tag color="success" style={{ margin: 0 }}>
                  Area: {(gamefenceArea / 10000).toFixed(2)} ha
                </Tag>
              )}
            </>
          )}
          {draggedMarker && dragDistance > 0 && (
            <Tag color="warning" style={{ margin: 0 }}>
              Dist: {dragDistance.toFixed(2)} m
            </Tag>
          )}
          {dragCoordinates && (
            <Tag color="success" style={{ margin: 0 }}>
              Lat: {dragCoordinates.lat.toFixed(6)}, Lng: {dragCoordinates.lng.toFixed(6)}
            </Tag>
          )}
          {selectedTool === 'border' && (
            <>
              <Tag style={{ margin: 0 }}>Pts: {borderPoints.length}</Tag>
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={clearBorder}
              >
                Clear
              </Button>
            </>
          )}
          {selectedTool === 'gamefence' && (
            <>
              <Tag style={{ margin: 0 }}>Pts: {gamefencePoints.length}</Tag>
              <span style={{ fontSize: '12px' }}>Pole:</span>
              <InputNumber
                value={poleDistance}
                onChange={(value) => savePoleDistance(value || 3)}
                min={0.1}
                step={0.1}
                style={{ width: '70px' }}
                addonAfter="m"
                size="small"
              />
              <Checkbox
                checked={gamefenceClosed}
                onChange={(e) => saveGamefenceClosed(e.target.checked)}
              >
                Closed
              </Checkbox>
              {totalFenceLength > 0 && poleCount > 0 && (
                <Tag color="blue" style={{ margin: 0 }}>Poles: {poleCount}</Tag>
              )}
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={clearGamefence}
              >
                Clear
              </Button>
              {polePositions.length > 0 && (
                <Button
                  type="primary"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => setShowPoleList(true)}
                >
                  Poles
                </Button>
              )}
            </>
          )}
          {selectedTool === 'croparea' && (
            <>
              <Tag style={{ margin: 0 }}>Pts: {cropAreaPoints.length}/2</Tag>
              <span style={{ fontSize: '12px' }}>W:</span>
              <InputNumber
                value={cropAreaWidth}
                onChange={(value) => saveCropAreaWidth(value || 10)}
                min={0.1}
                step={0.1}
                style={{ width: '70px' }}
                addonAfter="m"
                size="small"
              />
              <span style={{ fontSize: '12px' }}>⟂:</span>
              <InputNumber
                value={separationWidth}
                onChange={(value) => saveSeparationWidth(value || 0.5)}
                min={0}
                step={0.1}
                style={{ width: '70px' }}
                addonAfter="m"
                size="small"
              />
              <span style={{ fontSize: '12px' }}>∥:</span>
              <InputNumber
                value={separationWidthLongitudinal}
                onChange={(value) => saveSeparationWidthLongitudinal(value || 0.5)}
                min={0}
                step={0.1}
                style={{ width: '70px' }}
                addonAfter="m"
                size="small"
              />
              {cropAreaPoints.length === 1 && (
                <Tag color="blue" style={{ margin: 0 }}>Set end</Tag>
              )}
              {currentCropArea && (
                <>
                  <Tag style={{ margin: 0 }}>{currentCropArea.width.toFixed(1)}×{currentCropArea.length.toFixed(1)}m</Tag>
                  <Tag style={{ margin: 0 }}>{currentCropArea.area.toFixed(0)} m²</Tag>
                </>
              )}
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={clearCropAreaPoints}
              >
                Clear
              </Button>
            </>
          )}
        </Space>
      </div>
      <div className="app-layout" style={{ display: 'flex', gap: '20px', padding: '20px' }}>
        <Card
          title={<span style={{ color: theme.colors.primary }}>🌾 Tools</span>}
          style={{
            width: '240px',
            height: 'fit-content',
            borderColor: theme.colors.lightGrey,
          }}
          bodyStyle={{ padding: '12px' }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Button
              type={selectedTool === 'border' ? 'primary' : 'default'}
              danger={selectedTool === 'border'}
              onClick={() => selectTool('border')}
              icon={<BorderOutlined />}
              style={{ width: '100%', justifyContent: 'flex-start' }}
            >
              Border
            </Button>
            <Button
              type={selectedTool === 'gamefence' ? 'primary' : 'default'}
              onClick={() => selectTool('gamefence')}
              icon={<EnvironmentOutlined />}
              style={{
                width: '100%',
                justifyContent: 'flex-start',
                ...(selectedTool === 'gamefence' && {
                  backgroundColor: theme.colors.gamefence,
                  borderColor: theme.colors.gamefence
                })
              }}
            >
              Gamefence
            </Button>
            <Button
              type={selectedTool === 'croparea' ? 'primary' : 'default'}
              onClick={() => selectTool('croparea')}
              icon={<GoldOutlined />}
              style={{
                width: '100%',
                justifyContent: 'flex-start',
                ...(selectedTool === 'croparea' && {
                  backgroundColor: theme.colors.croparea,
                  borderColor: theme.colors.croparea
                })
              }}
            >
              Crop Area
            </Button>
            <Button
              type={selectedTool === 'plant' ? 'primary' : 'default'}
              onClick={() => selectTool('plant')}
              style={{
                width: '100%',
                justifyContent: 'flex-start',
                ...(selectedTool === 'plant' && {
                  backgroundColor: '#9370DB',
                  borderColor: '#9370DB',
                  color: 'white'
                })
              }}
            >
              🌱 Plants
            </Button>

          </Space>
          {/* Crop area subitems - only shown when croparea tool is selected */}
          {selectedTool === 'croparea' && cropAreas.length > 0 && (
            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${theme.colors.lightGrey}` }}>
              <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px', color: theme.colors.textSecondary }}>
                Crop Areas ({cropAreas.length})
              </div>
              {cropAreas.map((cropArea) => (
                <Card
                  key={cropArea.id}
                  size="small"
                  style={{
                    marginBottom: '8px',
                    cursor: 'pointer',
                    borderColor: selectedCropAreaId === cropArea.id ? theme.colors.primary : theme.colors.lightGrey,
                    backgroundColor: selectedCropAreaId === cropArea.id ? theme.colors.primaryPale : theme.colors.backgroundAlt,
                  }}
                  bodyStyle={{ padding: '8px' }}
                  onClick={() => handleSelectCropArea(cropArea.id)}
                >
                  {editingCropAreaId === cropArea.id ? (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Name"
                        size="small"
                        style={{ marginBottom: '6px' }}
                      />
                      <Input.TextArea
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        placeholder="Description"
                        size="small"
                        rows={2}
                        style={{ marginBottom: '6px' }}
                      />
                      <Space size="small">
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => handleSaveEditCropArea(cropArea.id)}
                          style={{ fontSize: '11px' }}
                        >
                          Save
                        </Button>
                        <Button
                          size="small"
                          onClick={handleCancelEditCropArea}
                          style={{ fontSize: '11px' }}
                        >
                          Cancel
                        </Button>
                      </Space>
                    </div>
                  ) : (
                    <>
                      <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '3px', color: theme.colors.textPrimary }}>
                        {cropArea.name}
                      </div>
                      {cropArea.description && (
                        <div style={{ fontSize: '11px', color: theme.colors.textSecondary, marginBottom: '4px' }}>
                          {cropArea.description}
                        </div>
                      )}
                      <div style={{ fontSize: '11px', color: theme.colors.textPrimary, marginBottom: '2px' }}>
                        {cropArea.area.toFixed(0)} m² ({(cropArea.area / 10000).toFixed(2)} ha)
                      </div>
                      <div style={{ fontSize: '10px', color: theme.colors.textLight, marginBottom: '6px' }}>
                        {cropArea.width.toFixed(1)}m × {cropArea.length.toFixed(1)}m
                      </div>
                      <Space size="small" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Edit">
                          <Button
                            type="primary"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleStartEditCropArea(cropArea)}
                            style={{ fontSize: '10px', padding: '0 6px' }}
                          />
                        </Tooltip>
                        <Tooltip title="Clone Perpendicular (⟂)">
                          <Button
                            size="small"
                            icon={<ArrowRightOutlined />}
                            onClick={() => handleCloneCropArea(cropArea)}
                            style={{ fontSize: '10px', padding: '0 6px', backgroundColor: theme.colors.success, color: 'white', borderColor: theme.colors.success }}
                          />
                        </Tooltip>
                        <Tooltip title="Clone Longitudinal (∥)">
                          <Button
                            size="small"
                            icon={<ArrowDownOutlined />}
                            onClick={() => handleCloneCropAreaLongitudinal(cropArea)}
                            style={{ fontSize: '10px', padding: '0 6px', backgroundColor: '#52c41a', color: 'white', borderColor: '#52c41a' }}
                          />
                        </Tooltip>
                        <Tooltip title="Clone Both Directions (⟂∥)">
                          <Button
                            size="small"
                            icon={<PlusSquareOutlined />}
                            onClick={() => handleCloneCropAreaBidirectional(cropArea)}
                            style={{ fontSize: '10px', padding: '0 6px', backgroundColor: '#faad14', color: 'white', borderColor: '#faad14' }}
                          />
                        </Tooltip>
                        <Tooltip title="Delete">
                          <Button
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteCropArea(cropArea.id)}
                            style={{ fontSize: '10px', padding: '0 6px' }}
                          />
                        </Tooltip>
                      </Space>
                    </>
                  )}
                </Card>
              ))}
            </div>
          )}
          {/* Plant tool controls and plant group list */}
          {selectedTool === 'plant' && (
            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${theme.colors.lightGrey}` }}>
              <div style={{ marginBottom: '12px' }}>
                <Button
                  type="primary"
                  onClick={() => {
                    if (cropAreas.length === 0) {
                      alert('Please create at least one crop area first');
                      return;
                    }
                    setEditingPlantGroup(null);
                    setShowPlantGroupModal(true);
                  }}
                  style={{
                    width: '100%',
                    backgroundColor: theme.colors.success,
                    borderColor: theme.colors.success
                  }}
                >
                  + Add Plant Group
                </Button>
              </div>

              {plantGroups.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <Button
                    onClick={() => setShowPlantListModal(true)}
                    style={{
                      width: '100%',
                      backgroundColor: theme.colors.primary,
                      borderColor: theme.colors.primary,
                      color: 'white'
                    }}
                  >
                    View All Plant Groups ({plantGroups.length})
                  </Button>
                </div>
              )}

              {plantGroups.length > 0 && (
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px', color: theme.colors.textSecondary }}>
                    Plant Groups ({plantGroups.length})
                  </div>
                  {plantGroups.map((plantGroup) => {
                    const slot = cropAreas.find(area => area.id === plantGroup.slotId);
                    return (
                      <Card
                        key={plantGroup.id}
                        size="small"
                        style={{
                          marginBottom: '8px',
                          cursor: 'pointer',
                          borderColor: selectedPlantGroupId === plantGroup.id ? '#9370DB' : theme.colors.lightGrey,
                          backgroundColor: selectedPlantGroupId === plantGroup.id ? '#f3f0ff' : theme.colors.backgroundAlt,
                        }}
                        bodyStyle={{ padding: '8px' }}
                        onClick={() => handleSelectPlantGroup(plantGroup.id)}
                      >
                        <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '3px', color: theme.colors.textPrimary }}>
                          🌱 {plantGroup.plantName}
                        </div>
                        {slot && (
                          <div style={{ fontSize: '11px', color: theme.colors.textSecondary, marginBottom: '3px' }}>
                            Slot: {slot.name}
                          </div>
                        )}
                        <div style={{ fontSize: '11px', color: theme.colors.textPrimary }}>
                          {plantGroup.totalPlants} plants ({plantGroup.actualRows}r × {plantGroup.actualCols}c)
                        </div>
                        <div style={{ fontSize: '10px', color: theme.colors.textLight }}>
                          {plantGroup.rowSpacing}m × {plantGroup.colSpacing}m spacing
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </Card>
        <div style={{ flex: 1 }}>
          <APIProvider apiKey="AIzaSyCZYZwuC7uhb4iTTkM4WpDT1znGPoa8d3w">
            <Map
              style={{ width: '100%', height: '500px' }}
              center={currentCenter}
              zoom={currentZoom}
              mapTypeId={currentMapTypeId}
              onClick={handleMapClick}
              onCenterChanged={handleCenterChanged}
              onZoomChanged={handleZoomChanged}
              onMapTypeIdChanged={handleMapTypeChanged}
              mapTypeControl={true}
            >
              {selectedTool === 'border' && borderPoints.map((point, index) => (
                <Marker
                  key={`border-${index}`}
                  position={point}
                  options={{
                    icon: {
                      path: 'M -4,-4 4,4 M 4,-4 -4,4',
                      strokeColor: '#FF0000',
                      strokeWeight: 3,
                      scale: 2
                    },
                    draggable: true
                  }}
                  onDragStart={() => handleMarkerDragStart(index, 'border')}
                  onDrag={(event) => handleMarkerDrag(index, event, 'border')}
                  onDragEnd={handleMarkerDragEnd}
                />
              ))}
              {selectedTool === 'gamefence' && gamefencePoints.map((point, index) => (
                <Marker
                  key={`gamefence-${index}`}
                  position={point}
                  options={{
                    icon: {
                      path: 'M -4,-4 4,4 M 4,-4 -4,4',
                      strokeColor: '#00FF00',
                      strokeWeight: 3,
                      scale: 2
                    },
                    draggable: true
                  }}
                  onDragStart={() => handleMarkerDragStart(index, 'gamefence')}
                  onDrag={(event) => handleMarkerDrag(index, event, 'gamefence')}
                  onDragEnd={handleMarkerDragEnd}
                />
              ))}
              {borderPoints.length > 1 && (
                <Polyline
                  path={borderPoints.length > 2 ? [...borderPoints, borderPoints[0]] : borderPoints}
                  options={{
                    strokeColor: '#FF0000',
                    strokeOpacity: 1.0,
                    strokeWeight: 2
                  }}
                />
              )}
              {gamefencePoints.length > 1 && (
                <Polyline
                  path={gamefenceClosed && gamefencePoints.length > 2 ? [...gamefencePoints, gamefencePoints[0]] : gamefencePoints}
                  options={{
                    strokeColor: '#00FF00',
                    strokeOpacity: 1.0,
                    strokeWeight: 2
                  }}
                />
              )}
              {/* Crop area temporary markers while drawing */}
              {selectedTool === 'croparea' && cropAreaPoints.map((point, index) => (
                <Marker
                  key={`croparea-temp-${index}`}
                  position={point}
                  options={{
                    icon: {
                      path: 'M -4,-4 4,4 M 4,-4 -4,4',
                      strokeColor: '#FFD700',
                      strokeWeight: 3,
                      scale: 2
                    },
                    draggable: false
                  }}
                />
              ))}
              {/* Crop area preview while drawing */}
              {selectedTool === 'croparea' && currentCropArea && (
                <Polygon
                  paths={currentCropArea.corners}
                  options={{
                    fillColor: '#FFD700',
                    fillOpacity: 0.2,
                    strokeColor: '#FFD700',
                    strokeOpacity: 0.8,
                    strokeWeight: 2
                  }}
                />
              )}
              {/* Render all saved crop areas */}
              {cropAreas.map((cropArea) => (
                <Polygon
                  key={cropArea.id}
                  paths={cropArea.corners}
                  options={{
                    fillColor: selectedCropAreaId === cropArea.id ? '#4CAF50' : '#FFD700',
                    fillOpacity: selectedCropAreaId === cropArea.id ? 0.4 : 0.2,
                    strokeColor: selectedCropAreaId === cropArea.id ? '#4CAF50' : '#FFD700',
                    strokeOpacity: selectedCropAreaId === cropArea.id ? 1.0 : 0.5,
                    strokeWeight: selectedCropAreaId === cropArea.id ? 3 : 2
                  }}
                  onClick={() => handleSelectCropArea(cropArea.id)}
                />
              ))}
              {/* Show label for selected crop area */}
              {selectedCropAreaId && cropAreas.find(a => a.id === selectedCropAreaId) && (
                <Marker
                  position={cropAreas.find(a => a.id === selectedCropAreaId).center}
                  options={{
                    label: {
                      text: cropAreas.find(a => a.id === selectedCropAreaId).name,
                      color: '#000000',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    },
                    icon: {
                      path: 'M 0,0',
                      scale: 1
                    }
                  }}
                />
              )}
              {/* Show markers for the 2 defining points when a crop area is selected */}
              {selectedCropAreaId && cropAreas.find(a => a.id === selectedCropAreaId) && cropAreas.find(a => a.id === selectedCropAreaId).points && (
                <>
                  {cropAreas.find(a => a.id === selectedCropAreaId).points.map((point, index) => (
                    <Marker
                      key={`croparea-point-${index}`}
                      position={point}
                      options={{
                        icon: {
                          path: 'M -4,-4 4,4 M 4,-4 -4,4',
                          strokeColor: '#4CAF50',
                          strokeWeight: 3,
                          scale: 2
                        },
                        draggable: true,
                        label: {
                          text: (index + 1).toString(),
                          color: '#FFFFFF',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }
                      }}
                      onDragStart={() => handleMarkerDragStart(index, 'croparea')}
                      onDrag={(event) => handleMarkerDrag(index, event, 'croparea')}
                      onDragEnd={handleMarkerDragEnd}
                    />
                  ))}
                </>
              )}
              {/* Render GPS Points */}
              {gpsPoints.map((gpsPoint) => (
                <Marker
                  key={`gps-${gpsPoint.id}`}
                  position={{ lat: gpsPoint.lat, lng: gpsPoint.lng }}
                  options={{
                    icon: {
                      path: 'M 0,-8 L 2,-2 L 8,0 L 2,2 L 0,8 L -2,2 L -8,0 L -2,-2 Z',
                      fillColor: '#17a2b8',
                      fillOpacity: 1,
                      strokeColor: '#FFFFFF',
                      strokeWeight: 2,
                      scale: 1
                    },
                    title: gpsPoint.name,
                    label: {
                      text: gpsPoint.name,
                      color: '#000000',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      className: 'gps-marker-label'
                    }
                  }}
                />
              ))}
              {/* Render plant markers for selected plant group */}
              {selectedPlantGroupId && (() => {
                const plantGroup = plantGroups.find(g => g.id === selectedPlantGroupId);
                if (!plantGroup) return null;

                const slot = cropAreas.find(area => area.id === plantGroup.slotId);
                if (!slot) return null;

                const plants = calculatePlantPositions(
                  slot,
                  plantGroup.rowSpacing,
                  plantGroup.colSpacing,
                  plantGroup.rowMargin ?? plantGroup.rowOffset ?? 0,
                  plantGroup.colMargin ?? plantGroup.colOffset ?? 0,
                  plantGroup.numRows,
                  plantGroup.numCols
                );

                return plants.map(plant => (
                  <Marker
                    key={`plant-${plantGroup.id}-${plant.index}`}
                    position={plant.position}
                    options={{
                      icon: {
                        path: 'M 0,0 m -3,0 a 3,3 0 1,0 6,0 a 3,3 0 1,0 -6,0',
                        fillColor: '#9370DB',
                        fillOpacity: 0.8,
                        strokeColor: '#6A5ACD',
                        strokeWeight: 1,
                        scale: 1
                      }
                    }}
                  />
                ));
              })()}
            </Map>
          </APIProvider>
        </div>
      </div>

      <PoleListModal
        showPoleList={showPoleList}
        polePositions={polePositions}
        poleDistance={poleDistance}
        totalFenceLength={totalFenceLength}
        gamefenceClosed={gamefenceClosed}
        gamefenceArea={gamefenceArea}
        copyFeedback={copyFeedback}
        onClose={() => setShowPoleList(false)}
        onCopyData={copyFenceData}
      />

      <GPSPointsModal
        showModal={showGpsPointsModal}
        gpsPoints={gpsPoints}
        onClose={() => setShowGpsPointsModal(false)}
        onAddPoint={handleAddGpsPoint}
        onEditPoint={handleEditGpsPoint}
        onDeletePoint={handleDeleteGpsPoint}
      />

      <PlantGroupModal
        show={showPlantGroupModal}
        onClose={() => {
          setShowPlantGroupModal(false);
          setEditingPlantGroup(null);
        }}
        onSave={handleSavePlantGroup}
        cropAreas={cropAreas}
        editPlantGroup={editingPlantGroup}
      />

      <PlantListModal
        show={showPlantListModal}
        onClose={() => setShowPlantListModal(false)}
        plantGroups={plantGroups}
        cropAreas={cropAreas}
        onEdit={handleEditPlantGroup}
        onDelete={handleDeletePlantGroup}
        onAddTodo={handleAddTodoForPlantGroup}
      />

      <TodoModal
        show={showTodoModal}
        onClose={() => {
          setShowTodoModal(false);
          setSelectedPlantGroupForTodo(null);
        }}
        onSave={handleSaveTodo}
        plantGroup={selectedPlantGroupForTodo}
      />
    </>
  );
}

export default MapComponent;